import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { node_imageBaseUrl } from "../pages/const";

/** png/jpg/webp/gif/pdf — matches customer_users KYC API */
const ACCEPT =
  "image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf";

/** Preview URL: {API_BASE}{path} e.g. https://api-syn.vedascodetech.in/uploads/customer_kyc/... */
function resolveDocUrl(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) return value;
  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  if (!base) return value;
  if (value.startsWith("/")) return `${base}${value}`;
  return `${base}/${value}`;
}

function isPdfSource(url, file) {
  if (file?.type === "application/pdf") return true;
  if (file?.name && /\.pdf$/i.test(file.name)) return true;
  const path = String(url || "").split("?")[0];
  return /\.pdf$/i.test(path);
}

function guessDownloadName(url, file, fallback = "document") {
  if (file?.name) return file.name;
  try {
    const path = String(url || "").split("?")[0];
    const part = path.split("/").filter(Boolean).pop();
    if (part) return decodeURIComponent(part);
  } catch {
    // ignore
  }
  return fallback;
}

/**
 * Text number input with an inline image/PDF upload control on the right
 * (same placement as a native date field's calendar icon).
 */
export default function DocNumberInput({
  label,
  controlId,
  value = "",
  onChange,
  placeholder,
  file = null,
  existingUrl = "",
  onFileChange,
  accept = ACCEPT,
}) {
  const autoId = useId();
  const inputId = controlId || autoId;
  const fileInputRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");

  const fileName = file?.name || "";
  const resolvedExistingUrl = resolveDocUrl(existingUrl);

  useEffect(() => {
    if (!(file instanceof File)) {
      setLocalPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const previewUrl = localPreviewUrl || resolvedExistingUrl;
  const hasDoc = Boolean(fileName || resolvedExistingUrl);
  const previewIsPdf = useMemo(
    () => isPdfSource(previewUrl, file),
    [previewUrl, file],
  );

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFilePick = (e) => {
    const next = e.target.files?.[0] || null;
    onFileChange?.(next);
    // Allow re-selecting the same file later
    e.target.value = "";
  };

  const clearFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onFileChange?.(null);
  };

  const openPreview = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!previewUrl) return;
    setShowPreview(true);
  };

  const closePreview = () => setShowPreview(false);

  const handleDownload = async () => {
    if (!previewUrl) return;
    const downloadName = guessDownloadName(previewUrl, file, "kyc-document");

    // Local File — direct object URL download
    if (file instanceof File && localPreviewUrl) {
      const link = document.createElement("a");
      link.href = localPreviewUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    try {
      const response = await fetch(previewUrl);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback if CORS blocks fetch
      const link = document.createElement("a");
      link.href = previewUrl;
      link.download = downloadName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  return (
    <>
      {label ? <Form.Label htmlFor={inputId}>{label}</Form.Label> : null}
      <div className="position-relative">
        <Form.Control
          id={inputId}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={{ paddingRight: hasDoc ? "4.5rem" : "2.5rem" }}
        />
        <div
          className="position-absolute top-50 translate-middle-y d-flex align-items-center gap-1"
          style={{ right: "0.5rem" }}
        >
          {hasDoc ? (
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-danger"
              onClick={clearFile}
              title="Remove document"
              aria-label="Remove document"
              style={{ lineHeight: 1 }}
            >
              <Icon icon="solar:close-circle-bold" width={18} height={18} />
            </button>
          ) : null}
          {previewUrl ? (
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-secondary"
              title="View document"
              aria-label="View document"
              style={{ lineHeight: 1 }}
              onClick={openPreview}
            >
              <Icon icon="solar:eye-bold" width={18} height={18} />
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-link p-0 border-0 text-dark"
            onClick={openFilePicker}
            title={hasDoc ? "Change image or PDF" : "Upload image or PDF"}
            aria-label={hasDoc ? "Change image or PDF" : "Upload image or PDF"}
            style={{ lineHeight: 1 }}
          >
            <Icon
              icon={
                hasDoc ? "solar:gallery-check-bold" : "solar:gallery-add-bold"
              }
              width={20}
              height={20}
            />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          hidden
          onChange={handleFilePick}
        />
      </div>
      {fileName ? (
        <Form.Text className="text-muted text-truncate d-block" title={fileName}>
          {fileName}
        </Form.Text>
      ) : null}

      {typeof document !== "undefined"
        ? ReactDOM.createPortal(
            <Modal
              show={showPreview}
              onHide={closePreview}
              centered
              size="lg"
              backdrop="static"
              style={{ zIndex: 1060 }}
              contentClassName="border-0 shadow-lg overflow-hidden"
              dialogClassName="kyc-doc-preview-dialog"
            >
              <Modal.Header
                closeButton
                closeVariant="white"
                className="border-0 text-white"
                style={{ background: "#1f2937" }}
              >
                <div className="d-flex align-items-center justify-content-between w-100 me-2 gap-2">
                  <Modal.Title className="fs-6 text-white mb-0 text-truncate">
                    {guessDownloadName(previewUrl, file, "Document preview")}
                  </Modal.Title>
                  <Button
                    variant="light"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={handleDownload}
                    disabled={!previewUrl}
                  >
                    <Icon
                      icon="solar:download-minimalistic-bold"
                      width={16}
                      height={16}
                      className="me-1"
                    />
                    Download
                  </Button>
                </div>
              </Modal.Header>
              <Modal.Body className="p-3" style={{ background: "#111827" }}>
                <div
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    minHeight: 320,
                    background: "#0b1220",
                    border: "1px solid #374151",
                  }}
                >
                  {previewUrl ? (
                    previewIsPdf ? (
                      <iframe
                        title="Document preview"
                        src={previewUrl}
                        style={{
                          width: "100%",
                          height: "70vh",
                          border: "none",
                          background: "#fff",
                          borderRadius: 6,
                        }}
                      />
                    ) : (
                      <img
                        src={previewUrl}
                        alt="Document preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "70vh",
                          objectFit: "contain",
                        }}
                      />
                    )
                  ) : (
                    <span className="text-white-50">No document to preview</span>
                  )}
                </div>
              </Modal.Body>
            </Modal>,
            document.body,
          )
        : null}
    </>
  );
}
